import React, { useRef, useState, lazy } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Avatar,
  Badge,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  useTheme,
} from "@mui/material";
import Blockies from "react-blockies";
import { TbUserPentagon, TbUserEdit, TbCarrot } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import CommonCard from "../CommonCard";
import "./index.css";
import NFTSelectPage from "./NFTSelectPage";
import { saveProfilePicture } from "../../../store/reducers/profile";
import EditPage from "./EditPage";
import useBasicProfile from "../../../hooks/useBasicProfile";
import { getProticoClient } from "../../../utils/api";
import { useWhiteboardRoom } from "../../../hooks/useWhiteboardRoom";
import { createClickEngagementRecorder } from "../../../utils/api";
// import UserAnalysis from "../AnalysisPage";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import useAlert from "../../../hooks/useAlert";
import { getFeatures } from "../../../config";
import { useWeb3Auth } from "../../../hooks/useWeb3Auth";
import AIAvatarPage from "../AIAvatarPage";
import SparklingAvatar from "../../SparklingAvatar";

const LazyUserAnalysis = lazy(() => import("../AnalysisPage"));

const EditablePage = () => {
  const { realAddress } = useWeb3Auth();
  const { roomDomain } = useWhiteboardRoom();
  const clickRecorder = createClickEngagementRecorder(roomDomain, roomDomain);
  const { isImgFetching, isUserProfileFetching } = useAppSelector(
    (state) => state.profile
  );
  const { currentAddress } = useAppSelector((state) => state.auth);
  const { basicProfile: userProfile, refetch } =
    useBasicProfile(currentAddress);
  const isLevel1 = userProfile?.aiavatar?.level === "level1";
  const uuid = getProticoClient().userInfo?.uuid;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [isOpen, alertContent, isError, setOptionalAlertContent] = useAlert();
  const [onEditProfile, setOnEditProfile] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showAIAvatar, setShowAIAvatar] = useState(false);
  const [anchorElEdit, setAnchorElEdit] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElNFT, setAnchorElNFT] = React.useState<null | HTMLElement>(
    null
  );
  const fileInput = useRef<HTMLInputElement | null>(null);

  const { FEATURE_USER_ANALYSIS_ENABLED } = getFeatures();

  const handleImgEditMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElEdit(event.currentTarget);
  };
  const handleCloseImgEditMenu = () => {
    setAnchorElEdit(null);
  };
  const handleImgInput = () => {
    clickRecorder("profile_edit");
    fileInput.current && fileInput.current.click();
    handleCloseImgEditMenu();
  };
  const handleOpenNFT = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNFT(event.currentTarget);
  };
  const handleCloseNFT = () => {
    setAnchorElNFT(null);
  };
  const getAvatar = () =>
    isImgFetching ? (
      <div
        style={{
          width: onEditProfile ? "10rem" : "5rem",
          height: onEditProfile ? "10rem" : "5rem",
          borderRadius: "50%",
          background: "#22222266",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress style={{ color: theme.brandColor }} size={20} />
      </div>
    ) : (
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        badgeContent={
          <Tooltip title={t("FlashChat_Profile_Avatar_Tooltip")}>
            <IconButton onClick={handleImgEditMenu} sx={{ p: 0 }}>
              <EditIcon sx={{ fontSize: 20 }} color="action" />
            </IconButton>
          </Tooltip>
        }
      >
        {userProfile.avatar_url ? (
          isLevel1 ? (
            <div
              className="editablePage-aiavatar-container"
              onClick={() => {
                clickRecorder("aiavatar_view");
                setShowAIAvatar(true);
              }}
            >
              <SparklingAvatar
                src={userProfile?.avatar_url}
                width="5rem"
                height="5rem"
              />
            </div>
          ) : (
            <Avatar
              alt="PFP"
              src={userProfile?.avatar_url}
              sx={{
                width: "5rem",
                height: "5rem",
              }}
            />
          )
        ) : (
          <Blockies
            seed={currentAddress ? currentAddress : uuid}
            size={10}
            scale={5}
            color="#ddd"
            className="identicon avator-big"
          />
        )}
      </Badge>
    );

  return (
    <div className="editablePage-container">
      {isUserProfileFetching || !userProfile ? (
        <CircularProgress style={{ color: theme.brandColor }} size={20} />
      ) : (
        <>
          <input
            type="file"
            ref={fileInput}
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files) {
                const file = e.target.files[0];
                dispatch(
                  saveProfilePicture(
                    file,
                    setOptionalAlertContent,
                    refetch,
                    realAddress
                  )
                );
              }
            }}
          />
          <Snackbar
            open={isOpen}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              variant="filled"
              severity={isError ? "error" : "success"}
              color={isError ? "error" : "info"}
            >
              <strong>{alertContent}</strong>
            </Alert>
          </Snackbar>
          <NFTSelectPage
            anchorEl={anchorElNFT}
            open={Boolean(anchorElNFT)}
            onClose={handleCloseNFT}
            handleImgInput={handleImgInput}
            handleSetAlert={setOptionalAlertContent}
          />
          <LazyUserAnalysis
            open={showAnalysis}
            onClose={() => setShowAnalysis(false)}
            userProfile={userProfile}
          />
          {showAIAvatar && (
            <AIAvatarPage
              onClose={() => setShowAIAvatar(false)}
              aiavatar={userProfile.aiavatar}
              isEditable={true}
            />
          )}
          {onEditProfile && getAvatar()}
          <Menu
            sx={{ mt: "40px" }}
            id="menu-appbar"
            anchorEl={anchorElEdit}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            open={Boolean(anchorElEdit)}
            onClose={handleCloseImgEditMenu}
          >
            <MenuItem
              style={{ fontSize: "12px", fontWeight: "600" }}
              onClick={(e) => {
                clickRecorder("profile_edit");
                handleOpenNFT(e);
                handleCloseImgEditMenu();
              }}
            >
              {t("FlashChat_Profile_Avatar_Pick")}
            </MenuItem>
            <MenuItem
              style={{ fontSize: "12px", fontWeight: "600" }}
              onClick={handleImgInput}
            >
              {t("FlashChat_Profile_Avatar_Upload")}
            </MenuItem>
          </Menu>
          {onEditProfile ? (
            <EditPage
              setOnEditProfile={setOnEditProfile}
              handleSetAlert={setOptionalAlertContent}
            />
          ) : (
            <>
              <div className="editablePage-user-info">
                <div className="editablePage-user-info-upper-block">
                  {getAvatar()}
                  <CommonCard
                    handleSetAlert={setOptionalAlertContent}
                    address={currentAddress}
                    basicProfile={userProfile}
                  />
                </div>
                <div className="editablePage-user-info-desc">
                  {userProfile.description}
                </div>
              </div>
              <div className="editButton-container">
                {isUserProfileFetching ? (
                  <CircularProgress
                    style={{ color: "#fff", marginTop: "5px", padding: "10px" }}
                    size={15}
                  />
                ) : (
                  <div
                    className="editButton"
                    onClick={() => {
                      clickRecorder("profile_edit");
                      setOnEditProfile(true);
                    }}
                  >
                    <TbUserEdit className="editButtonIcon" />
                    <span className="editButtonText">
                      {t("FlashChat_Profile_Edit_Button")}
                    </span>
                  </div>
                )}
              </div>
              <div className="editButton-container">
                <div
                  className="editButton"
                  style={{ background: "#FF9740" }}
                  onClick={() => {
                    clickRecorder("analysis_view");
                    setShowAIAvatar(true);
                  }}
                >
                  <TbCarrot className="editButtonIcon" />
                  <span className="editButtonText">
                    {t("AIAvatar.ViewButton")}
                  </span>
                </div>
              </div>
              {FEATURE_USER_ANALYSIS_ENABLED ? (
                <div className="editButton-container">
                  <div
                    className="editButton"
                    style={{
                      background: "#ff4081",
                      cursor: "default",
                      opacity: "0.4",
                    }}
                    onClick={() => {
                      clickRecorder("analysis_view");
                      // setShowAnalysis(true);
                    }}
                  >
                    <TbUserPentagon className="editButtonIcon" />
                    <span className="editButtonText">
                      {t("Analysis.ViewButton")}
                    </span>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
export default EditablePage;
